import { IMapService, ParkingSpot, UserLocation, MapBounds, GeolocationError } from './IMapService'

export class GoogleMapsService implements IMapService {
  private apiKey: string
  private map: google.maps.Map | null = null
  private markers: google.maps.Marker[] = []
  private lastLocation: (UserLocation & { method?: string }) | null = null
  private lastLocationTime: number = 0
  private readonly CACHE_DURATION = 30000 // 30 seconds

  constructor() {
    this.apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
  }

  async loadMap(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.google && window.google.maps) {
        resolve()
        return
      }

      // Check if already loading
      if ((window as any).googleMapsLoading) {
        // Wait for the existing loading promise
        ;(window as any).googleMapsLoading.then(resolve).catch(reject)
        return
      }

      // Check if API key is available
      if (!this.apiKey || this.apiKey === 'undefined') {
        reject(new Error('Google Maps API key not found. Please check your environment variables.'))
        return
      }

      // Check if script already exists
      const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`)
      if (existingScript) {
        // Script exists but Google Maps not ready yet, wait a bit
        const checkReady = () => {
          if (window.google && window.google.maps) {
            resolve()
          } else {
            setTimeout(checkReady, 100)
          }
        }
        checkReady()
        return
      }

      // Create loading promise
      const loadingPromise = new Promise<void>((resolveLoading, rejectLoading) => {
        const script = document.createElement('script')
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places`
        script.async = true
        script.defer = true
        
        script.onload = () => {
          delete (window as any).googleMapsLoading
          resolveLoading()
        }
        
        script.onerror = () => {
          delete (window as any).googleMapsLoading
          rejectLoading(new Error('Failed to load Google Maps. Please check your API key and internet connection.'))
        }
        
        document.head.appendChild(script)
      })

      // Store the loading promise
      ;(window as any).googleMapsLoading = loadingPromise
      
      loadingPromise.then(resolve).catch(reject)
    })
  }

  async initializeMap(element: HTMLElement, center: UserLocation): Promise<google.maps.Map> {
    const map = new google.maps.Map(element, {
      center: { lat: center.latitude, lng: center.longitude },
      zoom: 15,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ],
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: false,
      gestureHandling: 'greedy',
    })

    this.map = map
    return map
  }

  async getCurrentLocation(): Promise<UserLocation & { method?: string }> {
    if (!window.isSecureContext) {
      throw new GeolocationError('INSECURE_CONTEXT', 'La géolocalisation nécessite HTTPS sur iPhone/Safari.')
    }

    // Check cache
    const now = Date.now()
    if (this.lastLocation && (now - this.lastLocationTime < this.CACHE_DURATION)) {
      console.log('Returning cached location')
      return this.lastLocation
    }

    // Single robust request strategy
    try {
      if (!navigator.geolocation) {
        throw new GeolocationError('UNSUPPORTED', 'Geolocation not supported')
      }

      // We use a single call with a reasonable timeout and fallback
      const location = await new Promise<UserLocation & { method?: string }>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              method: position.coords.accuracy < 100 ? 'GPS (High Accuracy)' : 'Network/WiFi'
            })
          },
          (error) => {
            if (error.code === error.PERMISSION_DENIED) {
              reject(new GeolocationError('PERMISSION_DENIED', 'Permission de localisation refusée'))
              return
            }
            if (error.code === error.TIMEOUT) {
              reject(new GeolocationError('TIMEOUT', 'Délai de localisation dépassé'))
              return
            }
            if (error.code === error.POSITION_UNAVAILABLE) {
              reject(new GeolocationError('POSITION_UNAVAILABLE', 'Position indisponible'))
              return
            }
            reject(new GeolocationError('UNKNOWN', error.message || 'Erreur de géolocalisation inconnue'))
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000,
          }
        )
      })

      this.lastLocation = location
      this.lastLocationTime = now
      return location
    } catch (error) {
      if (error instanceof GeolocationError && error.code === 'PERMISSION_DENIED') {
        throw error
      }

      console.warn('Primary location method failed, trying IP fallback:', error)
      
      try {
        const location = await this.getIPLocation()
        const result = { ...location, method: 'IP Geolocation' }
        this.lastLocation = result
        this.lastLocationTime = now
        return result
      } catch (ipError) {
        console.error('All location methods failed:', ipError)
      }
    }

    throw new GeolocationError('POSITION_UNAVAILABLE', 'Impossible de déterminer votre position.')
  }

  private async getHighAccuracyLocation(): Promise<UserLocation> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'))
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
        },
        (error) => {
          reject(new Error(`High accuracy geolocation failed: ${error.message}`))
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 300000,
        }
      )
    })
  }

  private async getNetworkLocation(): Promise<UserLocation> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'))
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
        },
        (error) => {
          reject(new Error(`Network geolocation failed: ${error.message}`))
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 600000,
        }
      )
    })
  }

  private async getIPLocation(): Promise<UserLocation> {
    try {
      const services = [
        'https://ipapi.co/json/',
        'https://ip-api.com/json/',
        'https://ipinfo.io/json'
      ]

      for (const service of services) {
        try {
          const response = await fetch(service)
          if (!response.ok) continue
          
          const data = await response.json()
          
          let lat: number, lon: number
          
          if (service.includes('ipapi.co')) {
            lat = data.latitude
            lon = data.longitude
          } else if (service.includes('ip-api.com')) {
            lat = data.lat
            lon = data.lon
          } else if (service.includes('ipinfo.io')) {
            const [latStr, lonStr] = data.loc.split(',')
            lat = parseFloat(latStr)
            lon = parseFloat(lonStr)
          } else {
            continue
          }

          if (lat && lon && !isNaN(lat) && !isNaN(lon)) {
            return {
              latitude: lat,
              longitude: lon
            }
          }
        } catch (error) {
          console.warn(`IP location service ${service} failed:`, error)
          continue
        }
      }
      
      throw new Error('All IP location services failed')
    } catch (error) {
      throw new Error(`IP geolocation failed: ${error}`)
    }
  }

  async requestLocationPermission(): Promise<boolean> {
    try {
      if ('permissions' in navigator) {
        const permission = await navigator.permissions.query({ name: 'geolocation' })
        return permission.state === 'granted'
      }
      // If we cannot query permissions, we assume we need to ask, 
      // but we don't want to trigger a prompt here, we let getCurrentLocation do it.
      return false 
    } catch (error) {
      return false
    }
  }

  getMapBounds(map: google.maps.Map): MapBounds {
    const bounds = map.getBounds()
    if (!bounds) {
      return { center: { latitude: 40.7128, longitude: -74.0060 }, radius: 2000 }
    }

    const center = bounds.getCenter()
    const ne = bounds.getNorthEast()

    const radius = this.calculateDistance(
      center.lat(),
      center.lng(),
      ne.lat(),
      ne.lng()
    )

    const limitedRadius = Math.min(radius, 10000)

    return {
      center: { latitude: center.lat(), longitude: center.lng() },
      radius: limitedRadius
    }
  }

  setMapCenter(map: google.maps.Map, location: UserLocation): void {
    map.setCenter({ lat: location.latitude, lng: location.longitude })
  }

  setMapZoom(map: google.maps.Map, zoom: number): void {
    map.setZoom(zoom)
  }

  addMarker(map: google.maps.Map, position: UserLocation, title: string, isUser: boolean = false, variant?: 'default' | 'parked' | 'selected'): google.maps.Marker {
    let icon;

    if (variant === 'selected') {
      icon = {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="72" height="72" viewBox="0 0 72 72" xmlns="http://www.w3.org/2000/svg">
            <circle cx="36" cy="36" r="18" fill="#10B981" opacity="0.25">
              <animate attributeName="r" from="18" to="32" dur="1.8s" repeatCount="indefinite"/>
              <animate attributeName="opacity" from="0.25" to="0" dur="1.8s" repeatCount="indefinite"/>
            </circle>
            <circle cx="36" cy="36" r="12" fill="#10B981" opacity="0.35">
              <animate attributeName="r" from="12" to="26" dur="1.8s" begin="0.4s" repeatCount="indefinite"/>
              <animate attributeName="opacity" from="0.35" to="0" dur="1.8s" begin="0.4s" repeatCount="indefinite"/>
            </circle>
            <circle cx="36" cy="36" r="16" fill="#10B981" stroke="white" stroke-width="3"/>
            <path d="M44.5 39H43V35.8C43 34.26 41.74 33 40.2 33H31.8C30.26 33 29 34.26 29 35.8V39H27.5C26.95 39 26.5 39.45 26.5 40V43.5C26.5 44.05 26.95 44.5 27.5 44.5H28V45.2C28 46.19 28.81 47 29.8 47C30.79 47 31.6 46.19 31.6 45.2V44.5H40.4V45.2C40.4 46.19 41.21 47 42.2 47C43.19 47 44 46.19 44 45.2V44.5H44.5C45.05 44.5 45.5 44.05 45.5 43.5V40C45.5 39.45 45.05 39 44.5 39ZM31 36.2C31 35.87 31.27 35.6 31.6 35.6H40.4C40.73 35.6 41 35.87 41 36.2V38.4C41 38.73 40.73 39 40.4 39H31.6C31.27 39 31 38.73 31 38.4V36.2Z" fill="white"/>
          </svg>
        `),
        scaledSize: new google.maps.Size(72, 72),
        anchor: new google.maps.Point(36, 36),
      }
    } else if (variant === 'parked') {
      icon = {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="11" fill="#3B82F6" stroke="white" stroke-width="2"/>
            <path d="M18.5 13H17V10C17 8.9 16.1 8 15 8H9C7.9 8 7 8.9 7 10V13H5.5C5.22 13 5 13.22 5 13.5V16.5C5 16.78 5.22 17 5.5 17H6V18C6 18.55 6.45 19 7 19C7.55 19 8 18.55 8 18V17H16V18C16 18.55 16.45 19 17 19C17.55 19 18 18.55 18 18V17H18.5C18.78 17 19 16.78 19 16.5V13.5C19 13.22 18.78 13 18.5 13ZM8.5 10.5C8.5 10.22 8.72 10 9 10H15C15.28 10 15.5 10.22 15.5 10.5V12.5C15.5 12.78 15.28 13 15 13H9C8.72 13 8.5 12.78 8.5 12.5V10.5Z" fill="white"/>
          </svg>
        `),
        scaledSize: new google.maps.Size(40, 40),
      }
    } else if (isUser) {
      icon = {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
            <circle cx="30" cy="30" r="10" fill="#00B04F" stroke="white" stroke-width="2"/>
            <circle cx="30" cy="30" r="10" fill="#00B04F" opacity="0.6">
              <animate attributeName="r" from="10" to="25" dur="1.5s" repeatCount="indefinite" />
              <animate attributeName="opacity" from="0.6" to="0" dur="1.5s" repeatCount="indefinite" />
            </circle>
          </svg>
        `),
        scaledSize: new google.maps.Size(60, 60),
        anchor: new google.maps.Point(30, 30),
      }
    } else {
      icon = {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#00B04F"/>
            <circle cx="12" cy="9" r="2.5" fill="white"/>
          </svg>
        `),
        scaledSize: new google.maps.Size(32, 32),
      }
    }

    const marker = new google.maps.Marker({
      position: { lat: position.latitude, lng: position.longitude },
      map: map,
      title: title,
      icon: icon,
    })

    this.markers.push(marker)
    return marker
  }

  removeMarker(marker: google.maps.Marker): void {
    marker.setMap(null)
    const index = this.markers.indexOf(marker)
    if (index > -1) {
      this.markers.splice(index, 1)
    }
  }

  clearMarkers(markers: google.maps.Marker[]): void {
    markers.forEach(marker => {
      marker.setMap(null)
      const index = this.markers.indexOf(marker)
      if (index > -1) {
        this.markers.splice(index, 1)
      }
    })
  }

  async searchNearbyParkings(location: UserLocation, radius: number = 2000): Promise<ParkingSpot[]> {
    return new Promise((resolve) => {
      if (!this.map) {
        console.error('Map not initialized')
        resolve([])
        return
      }

      const service = new google.maps.places.PlacesService(this.map)
      
      const request: google.maps.places.PlaceSearchRequest = {
        location: new google.maps.LatLng(location.latitude, location.longitude),
        radius: Math.min(radius, 50000),
        type: 'parking'
      }

      service.nearbySearch(request, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          const parkingSpots: ParkingSpot[] = results.map((place) => ({
            place_id: place.place_id || '',
            name: place.name || 'Unnamed Parking',
            address: place.vicinity || place.formatted_address || 'Address not available',
            latitude: place.geometry?.location?.lat() || 0,
            longitude: place.geometry?.location?.lng() || 0,
            rating: place.rating,
            price_level: place.price_level,
            opening_hours: place.opening_hours ? {} : undefined,
            photos: place.photos ? place.photos.slice(0, 3).map((photo) => 
              photo.getUrl({ maxWidth: 400, maxHeight: 300 })
            ) : undefined,
          }))

          parkingSpots.forEach(spot => {
            spot.distance = this.calculateDistance(
              location.latitude,
              location.longitude,
              spot.latitude,
              spot.longitude
            )
          })

          const filteredSpots = parkingSpots
            .filter(spot => (spot.distance || 0) <= radius)
            .sort((a, b) => (a.distance || 0) - (b.distance || 0))

          resolve(filteredSpots)
        } else {
          console.error('Places search failed:', status)
          resolve([])
        }
      })
    })
  }

  async searchParkingsInBounds(map: google.maps.Map, userLocation?: UserLocation): Promise<ParkingSpot[]> {
    const { center, radius } = this.getMapBounds(map)
    
    const parkingSpots = await this.searchNearbyParkings(center, radius)
    
    const referenceLocation = userLocation || center
    parkingSpots.forEach(spot => {
      spot.distance = this.calculateDistance(
        referenceLocation.latitude,
        referenceLocation.longitude,
        spot.latitude,
        spot.longitude
      )
    })
    
    return parkingSpots.sort((a, b) => (a.distance || 0) - (b.distance || 0))
  }

  calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371
    const dLat = this.deg2rad(lat2 - lat1)
    const dLon = this.deg2rad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const d = R * c
    return Math.round(d * 1000)
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180)
  }

  openDirections(destination: ParkingSpot): void {
    // Détecter le type d'appareil et utiliser l'app de navigation appropriée
    const lat = destination.latitude
    const lon = destination.longitude
    // Détecter iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    // Détecter Android
    const isAndroid = /Android/.test(navigator.userAgent)
    
    if (isIOS) {
      // Sur iOS, essayer d'ouvrir Apple Maps en premier
      const appleMapsUrl = `maps://maps.apple.com/?daddr=${lat},${lon}&dirflg=d`
      window.location.href = appleMapsUrl
      
      // Fallback vers Google Maps si Apple Maps n'est pas disponible
      setTimeout(() => {
        const googleMapsUrl = `https://maps.google.com/maps?daddr=${lat},${lon}&dirflg=d`
        window.open(googleMapsUrl, '_blank')
      }, 1000)
    } else if (isAndroid) {
      // Sur Android, utiliser l'intent Google Maps
      const googleMapsIntent = `google.navigation:q=${lat},${lon}&mode=d`
      window.location.href = googleMapsIntent
      
      // Fallback vers l'URL web Google Maps
      setTimeout(() => {
        const googleMapsUrl = `https://maps.google.com/maps?daddr=${lat},${lon}&dirflg=d`
        window.open(googleMapsUrl, '_blank')
      }, 1000)
    } else {
      // Sur desktop ou autres plateformes, utiliser Google Maps web
      const googleMapsUrl = `https://maps.google.com/maps?daddr=${lat},${lon}&dirflg=d`
      window.open(googleMapsUrl, '_blank')
    }
  }

  cleanup(): void {
    this.clearMarkers(this.markers)
    this.map = null
    this.markers = []
  }
}
